import { getIpawsJurisdictionBoundaryRings, getIpawsJurisdictionPolygons } from './ipawsArea.utils';
import { formatChannelNames } from './ipawsChannelRules.utils';

/**
 * The areas the workplace's COG may alert — `GET /fusion/v2/fema/jurisdictions`, one entry per
 * SAME code in the COG Profile FEMA issues (card BE-13), with the dissemination channels that
 * hold it and the county's Census ring. Pure helpers for the selector, the map and the check.
 */

const placeName = (jurisdiction) =>
  jurisdiction?.state ? `${jurisdiction.name}, ${jurisdiction.state}` : (jurisdiction?.name ?? '');

/**
 * Lists only the channels FEMA grants to the area, with WEA as the operator-facing CMAS name.
 */
export const jurisdictionOptionLabel = (jurisdiction) => {
  const order = ['CMAS', 'EAS', 'NWEM', 'PUBLIC', 'CAPEXCH'];
  const displayNames = { CMAS: 'WEA' };
  const grants = Array.isArray(jurisdiction?.channels) ? jurisdiction.channels : [];
  const channels = order
    .filter((channel) => grants.includes(channel))
    .map((channel) => displayNames[channel] ?? channel)
    .join(', ');
  return `${placeName(jurisdiction)} · SAME ${jurisdiction?.sameCode ?? '?'} · ${channels || 'no channel'}`;
};

export const authorizedJurisdictionFor = (jurisdictions, sameCode) =>
  (Array.isArray(jurisdictions) ? jurisdictions : []).find(
    (jurisdiction) => jurisdiction?.sameCode === sameCode,
  ) ?? null;

export const isAuthorizedSameCode = (jurisdictions, sameCode) =>
  Boolean(sameCode && authorizedJurisdictionFor(jurisdictions, sameCode));

/**
 * What the draft keeps when an authorized area is picked — the same shape a searched county
 * leaves (`describeResolvedJurisdiction`), plus the code and channels FEMA authorized.
 */
export const resolvedFromAuthorized = (jurisdiction) => {
  if (!jurisdiction) return null;
  return {
    GEOID: String(jurisdiction.geoid ?? ''),
    name: placeName(jurisdiction),
    place_id: `fema-${jurisdiction.sameCode}`,
    lookup_type: jurisdiction.wholeState ? 'state' : 'county',
    sameCode: jurisdiction.sameCode,
    channels: Array.isArray(jurisdiction.channels) ? jurisdiction.channels : [],
    boundaryRings: getIpawsJurisdictionBoundaryRings(jurisdiction),
  };
};

/** The draft's SAME code, or '' when none is set. */
export const draftSameCode = (draft) =>
  draft?.geocodes?.find((code) => code?.valueName === 'SAME' && code?.value)?.value ?? '';

/**
 * The finding when the draft's SAME code is not one the COG holds — FEMA answers 305/219 to
 * such an alert. Silent while the authorized list is unknown (loading, failed, or empty).
 */
export const unauthorizedGeocodeFinding = (draft, jurisdictions) => {
  const code = draftSameCode(draft);
  if (!code || !Array.isArray(jurisdictions) || jurisdictions.length === 0) return null;
  if (isAuthorizedSameCode(jurisdictions, code)) return null;
  return `SAME ${code} is not in the COG's authorized areas; FEMA refuses it. Pick an authorized area.`;
};

/**
 * Why the authorized areas could not be read, from the edge's answer: its own message, the
 * service's code or message when it relayed one, else the HTTP status. Never a bare "failed".
 */
export const authorizedAreasFailureReason = (error) => {
  // axiosBaseQuery hands RTK { status, message, raw: <the body> }; a plain { status, data } is
  // read too, so a mocked or fetch-shaped error still explains itself.
  const data = error?.raw ?? error?.data;
  const service = data?.service;
  const parts = [];
  const message = typeof data?.message === 'string' ? data.message : error?.message;
  if (typeof message === 'string' && message.trim()) parts.push(message.trim());
  if (typeof service?.code === 'string' && service.code.trim()) parts.push(service.code.trim());
  if (typeof service?.message === 'string' && service.message.trim()) {
    parts.push(service.message.trim());
  }
  if (!parts.length && typeof data?.error === 'string' && data.error.trim()) {
    parts.push(data.error.trim());
  }
  if (!parts.length) {
    parts.push(
      Number.isFinite(Number(error?.status))
        ? `HTTP ${error.status}${
            Number(error.status) === 404
              ? ' — this backend has no /v2/fema/jurisdictions route (restart it on a build that has it)'
              : ''
          }`
        : 'No answer from the server',
    );
  }
  return parts.join(' · ');
};

/** The rings to draw for every authorized area, the draft's own marked as selected. */
export const authorizedAreaRings = (jurisdictions, selectedSameCode) =>
  (Array.isArray(jurisdictions) ? jurisdictions : [])
    .map((jurisdiction) => ({
      key: jurisdiction.sameCode,
      name: placeName(jurisdiction),
      selected: jurisdiction.sameCode === selectedSameCode,
      rings: getIpawsJurisdictionBoundaryRings(jurisdiction),
    }))
    .filter((area) => area.rings.length > 0);

/** Returns Leaflet bounds containing every ring of a resolved FEMA jurisdiction. */
export const getFemaJurisdictionBounds = (jurisdiction) => {
  const coordinates = (jurisdiction?.boundaryRings ?? [])
    .flat()
    .filter(
      (coordinate) =>
        Number.isFinite(Number(coordinate?.lat)) && Number.isFinite(Number(coordinate?.lng)),
    );
  if (!coordinates.length) return undefined;

  const latitudes = coordinates.map(({ lat }) => Number(lat));
  const longitudes = coordinates.map(({ lng }) => Number(lng));

  return [
    [Math.min(...latitudes), Math.min(...longitudes)],
    [Math.max(...latitudes), Math.max(...longitudes)],
  ];
};

/**
 * The area the draft's map shapes must stay inside: the authorized area its SAME code targets.
 * This is the rule since 2026-08-28 (Mike: "we need to be allowed to send in the fema
 * jurisdictions even if its outside of our workplace jurisdiction") — the workplace's own
 * licensed zones play no part. `ready` is false, with the reason, whenever the check cannot run:
 * no target picked, the list still loading or failed, a code the COG does not hold (its own
 * finding says so), or an authorized target whose ring this server does not hold (a state-wide
 * code before the Census state table is loaded). Not ready never means "draw anywhere": the
 * backend runs the same check and records a refusal.
 */
export const permittedAreaForDraft = (
  draft,
  jurisdictions,
  { isLoading = false, isError = false } = {},
) => {
  const sameCode = draftSameCode(draft);
  const notReady = (reason, label = '') => ({
    sameCode,
    label,
    rings: [],
    polygons: [],
    ready: false,
    reason,
  });
  if (!sameCode) {
    return notReady(
      'Pick the authorized area the alert targets; map shapes are checked against its boundary.',
    );
  }
  if (isLoading) {
    return notReady(
      'The authorized areas are still loading, so the map shapes cannot be checked yet.',
    );
  }
  if (isError || !Array.isArray(jurisdictions) || jurisdictions.length === 0) {
    return notReady(
      'The authorized areas could not be read, so the map shapes cannot be checked for IPAWS submission.',
    );
  }
  const jurisdiction = authorizedJurisdictionFor(jurisdictions, sameCode);
  if (!jurisdiction) {
    return notReady(
      `SAME ${sameCode} is not an authorized area, so the map shapes have nothing to be checked against.`,
    );
  }
  const label = `${placeName(jurisdiction)} (SAME ${sameCode})`;
  const rings = getIpawsJurisdictionBoundaryRings(jurisdiction);
  if (!rings.length) {
    return notReady(
      `The boundary of ${label} is not available on this server, so the map shapes cannot be checked. ` +
        'Target a county, or load the Census state boundaries (backend `deno task census:states`).',
      label,
    );
  }
  // `rings` = the outer rings (drawn); `polygons` = rings with holes (checked, as the backend does).
  return {
    sameCode,
    label,
    rings,
    polygons: getIpawsJurisdictionPolygons(jurisdiction),
    ready: true,
    reason: '',
  };
};
